import { camelCase } from 'change-case';

import { constants as config } from '@config';

import { transformNumberToStyle } from '../../../../../../../src/utils/format';
import { createSvgElement } from '../../../../../helpers/getPageContent';

const template = require('./blank-design.hbs');
export default class TableOfContentsGenerator {
  #tool;
  #editor;
  #canvas;
  #context;

  #defaultWidth;
  #defaultHeight;
  #defaultHeadingStyle;
  #settings;

  #positions;
  #canvasWidth;
  #canvasHeight;
  #offsetPercent;
  #elementPadding;
  #offset;
  #maxHeadingWidthPercent;
  #ratio;

  #xTracking;
  #yTracking;

  #tableOfContentsPages;
  #maxLevel;
  #currentPage;
  #templateElement;
  #headingsSummary;
  #pageNumberOffset;
  #currentPageIndex;

  constructor () {
    this.#tool = INKIVE.Tool.getInstance();
    this.#editor = this.#tool.editor;
    this.#canvas = this.#editor.canvas;
    this.#context = null;

    this.#defaultWidth = config.tableOfContents.defaultCanvasWidth;
    this.#defaultHeight = config.tableOfContents.defaultCanvasHeight;
    this.#defaultHeadingStyle = config.tableOfContents.defaultHeadingStyle;
    this.#settings = config.tableOfContents;
    this.#headingsSummary = [];
    this.#positions = [];

    this.#canvasWidth = 0;
    this.#canvasHeight = 0;
    this.#offsetPercent = 0;
    this.#elementPadding = 0;
    this.#offset = 0;
    this.#ratio = 1;

    this.#xTracking = 0;
    this.#yTracking = 0;
    this.#pageNumberOffset = 0;

    this.#tableOfContentsPages = [];
    this.#maxLevel = 0;
    this.#currentPageIndex = 0;
    this.#currentPage = null;
    this.#templateElement = null;
  }

  initialize (settings, headingsSummary, { width, height, unit, originalDimensions = {} }, tableOfContentsPages = [], positions = []) {
    this.#positions = positions;
    this.#calculateTableMetrics(width, height);

    this.#context = document.querySelector('canvas').getContext('2d');

    this.#templateElement = createSvgElement(template({
      width: width || this.defaultWidth,
      height: height || this.defaultHeight,
      originalHeight: originalDimensions?.height || this.#canvasHeight,
      originalWidth: originalDimensions?.width || this.#canvasWidth,
      originalUnit: originalDimensions?.unit || unit
    })).firstChild;
    this.#tableOfContentsPages = this.#getTableOfContentsPages(tableOfContentsPages);
    this.#maxLevel = parseInt(settings.levels);
    this.#currentPage = this.#tableOfContentsPages[this.#currentPageIndex];

    this.#settings = settings || config.tableOfContents;
    this.#headingsSummary = headingsSummary;
    this.#pageNumberOffset = 0;

    return this;
  }

  #getTableOfContentsPages (tableOfContentsPages) {
    const existedPages = tableOfContentsPages.map(page => page.cloneNode(true));
    const blankPages = [];
    for (let i = 0; i < config.tableOfContents.maxPages; i++) {
      const blankPage = this.#templateElement.cloneNode(true);
      blankPages.push(blankPage);
    }
    return existedPages.concat(blankPages).slice(0, config.tableOfContents.maxPages);
  }

  #calculateLongestMarginLeft (headings) {
    let longestMargin = 0;
    for (const heading of headings) {
      const style = this.#getStyle(heading);
      const textarea = {
        font_size: style.fontSize,
        font_family: style.fontFamily,
        font_weight: style.fontWeight,
        styles: []
      };

      const prefixWidth = heading.prefix
        ? INKIVE.Text.Helpers.getLineBBox(textarea, 0, heading.prefix).width
        : 0;

      longestMargin = Math.max(longestMargin, prefixWidth);
    }

    return longestMargin;
  }

  #calculateHeadingMarginLeft () {
    if (!this.#headingsSummary?.length) return;

    const levelListWidth = [];
    const maxLevel = Math.min(this.#maxLevel, Math.max(...this.#headingsSummary.map(heading => heading.heading)));

    for (let index = 1; index <= maxLevel; index++) {
      const queryHeadings = this.#headingsSummary.filter(heading => heading.heading === index);
      const longestMargin = this.#calculateLongestMarginLeft(queryHeadings);
      levelListWidth.push(longestMargin);
    }

    const levelMargin = levelListWidth.slice();
    for (let index = 0; index < levelMargin.length; index++) {
      const level = index + 1;
      if (index > 0 && levelListWidth[index - 1]) {
        levelMargin[index] = levelMargin[index - 1] + levelListWidth[index - 1];
      } else {
        levelMargin[index] = this.#defaultHeadingStyle[level].marginLeft * this.#ratio;
      }
    }

    for (const heading of this.#headingsSummary) {
      heading.marginLeft = levelMargin[heading.heading - 1] || 0;
      heading.listWidth = levelListWidth[heading.heading - 1] || 0;
    }
  }

  getNumberOfPages () {
    if (!this.#headingsSummary.length) {
      this.#removeRedundantPages();
      return this.#tableOfContentsPages.length;
    }
    this.#appendTitleToTable();
    this.#traverseHeadings();
    return this.#tableOfContentsPages.length;
  }

  drawTableOfContents (pageNumberOffset) {
    this.#pageNumberOffset = pageNumberOffset;
    if (!this.#headingsSummary.length) {
      this.#removeRedundantPages();
      return this.#tableOfContentsPages;
    }
    this.#appendTitleToTable();
    this.#calculateHeadingMarginLeft();
    this.#traverseHeadings();

    return this.#tableOfContentsPages;
  }

  #calculateTableMetrics (width, height) {
    const position = this.#positions[this.#currentPageIndex] || {};

    this.#canvasWidth = position.width || width || this.#defaultWidth;
    this.#canvasHeight = position.height || height || this.#defaultHeight;
    this.#maxHeadingWidthPercent = config.tableOfContents.maxHeadingWidthPercent;

    // this.#ratio = (this.#canvasWidth * this.#canvasHeight) / (this.#defaultWidth * this.#defaultHeight);
    this.#ratio = this.#canvasWidth > this.#canvasHeight
      ? this.#canvasWidth / this.#defaultWidth
      : this.#canvasHeight / this.#defaultHeight;

    this.#offsetPercent = config.tableOfContents.offsetPercent;

    if (position.width || position.height) {
      this.#ratio = 1;
      this.#offsetPercent = 0;
      this.#maxHeadingWidthPercent = 1;
    }

    this.#offset = this.#canvasWidth * this.#offsetPercent;
    this.#xTracking = !isNaN(position.x) ? position.x : this.#canvasWidth * this.#offsetPercent;
    this.#yTracking = !isNaN(position.y) ? position.y : this.#canvasHeight * this.#offsetPercent;
    this.#elementPadding = config.tableOfContents.elementPadding * this.#ratio;
  }

  #removeRedundantPages () {
    this.#tableOfContentsPages = this.#tableOfContentsPages.filter(page => !!page.querySelector('text'));
    this.#tableOfContentsPages = this.#tableOfContentsPages.slice(0, config.tableOfContents.maxPages);
  }

  #traverseHeadings () {
    // pre-order traverse an N-ary tree without recursion
    const headings = [{ child: this.#headingsSummary }];
    while (headings.length !== 0) {
      const currentHeading = headings.pop();
      if (typeof currentHeading === 'undefined' || currentHeading.heading > this.#maxLevel) {
        continue;
      }
      const content = (currentHeading.title || '') + (currentHeading.prefix || '');
      content && this.#appendHeadingToTable(currentHeading);
      for (let i = currentHeading.child.length - 1; i >= 0; i--) {
        headings.push(currentHeading.child[i]);
      }
    }
    this.#removeRedundantPages();
  }

  #appendTitleToTable () {
    const heading = this.#getTitleFromHeadingSummary();

    const style = this.#calculateTableStyle(heading);

    this.#drawHeadingTitle(heading, style, config.tableOfContents.extraRectWidthForTitle);
    this.#recalculatePositionTracking(style);
  }

  #getTitleFromHeadingSummary () {
    const hashFontFamily = {
      Nunito: 1
    };

    const hashFillColor = {
      '#000000': 1
    };

    const headings = [{ child: this.#headingsSummary }];
    while (headings.length !== 0) {
      const currentHeading = headings.pop();

      if (typeof currentHeading === 'undefined') continue;

      if (currentHeading.fill) {
        hashFillColor[currentHeading.fill] = hashFillColor[currentHeading.fill] ? hashFillColor[currentHeading.fill] + 1 : 1;
      }

      if (currentHeading.font_family) {
        hashFontFamily[currentHeading.font_family] = hashFontFamily[currentHeading.font_family] ? hashFontFamily[currentHeading.font_family] + 1 : 1;
      }

      for (let i = currentHeading.child.length - 1; i >= 0; i--) {
        headings.push(currentHeading.child[i]);
      }
    }

    const titleHeading = {
      title: 'Table of Contents',
      heading: 0,
      font_family: Object.keys(hashFontFamily).reduce(function (a, b) { return hashFontFamily[a] > hashFontFamily[b] ? a : b; }),
      fill: Object.keys(hashFillColor).reduce(function (a, b) { return hashFillColor[a] > hashFillColor[b] ? a : b; })
    };

    return titleHeading;
  }

  #appendHeadingToTable (heading) {
    if (heading.heading > this.#settings.levels) return;
    if (this.#currentPageIndex > config.tableOfContents.maxPages - 1) return;

    const style = this.#calculateTableStyle(heading);

    this.#yTracking += style.marginTop;

    let iteration = 0;
    let isSplitTextBox = this.#drawHeadingTitle(heading, style);
    while (isSplitTextBox && iteration < config.tableOfContents.maxPages) {
      this.#recalculatePositionTracking(style);
      if (this.#currentPageIndex > config.tableOfContents.maxPages - 1) return;

      style.textBox = isSplitTextBox;
      style.isSplit = true;
      isSplitTextBox = this.#drawHeadingTitle(heading, style);
      iteration++;
    }

    this.#drawPageNumber(heading, style);
    this.#drawLeader(heading, style);

    this.#recalculatePositionTracking(style);
  }

  #isLongTextCalculation (content, fontSize, letterSpacing) {
    const fontSizeToTextWidthFactor = 0.6;
    const numberOfLettersHaveSpacing = content.length - 2;
    const approximateTextWidth = content.length * fontSize * fontSizeToTextWidthFactor + letterSpacing * numberOfLettersHaveSpacing;
    const canvasWidth = this.#canvasWidth * this.#maxHeadingWidthPercent;
    return approximateTextWidth > canvasWidth;
  }

  #calculateTableStyle (heading) {
    const textStyle = this.#getStyle(heading);
    const pageNumberFontSize = this.#getPageNumberFontSize(heading);
    const marginLeft = this.#getMarginLeft(heading);
    const marginBottom = this.#getMarginBottom(heading);
    const marginTop = this.#getMarginTop(heading);
    const isUpperCase = this.#getIsUpperCase(heading);
    const letterSpacing = this.#getLetterSpacing(heading);
    const listWidth = heading.listWidth || 0;
    const title = (heading.prefix || '') + heading.title;
    const textBox = this.#isLongTextCalculation(title, textStyle.fontSize, letterSpacing)
      ? this.#calculateLongTextBox(title, { ...textStyle, isUpperCase, letterSpacing, marginLeft, listWidth })
      : this.#calculateTextBox(title, { ...textStyle, isUpperCase, letterSpacing, marginLeft });
    const pageNumber = this.#offsetPageNumber(heading);
    const pageNumberBox = this.#calculateTextBox(pageNumber, { ...textStyle, fontSize: pageNumberFontSize, isUpperCase, letterSpacing: 0, marginLeft });

    return { ...textStyle, pageNumberFontSize, marginLeft, marginBottom, marginTop, textBox, pageNumberBox, letterSpacing, listWidth };
  }

  #drawHeadingTitle (heading, { fontWeight, fontSize, fontFamily, fill, marginLeft, textBox, letterSpacing, listWidth, listFill, isSplit }, extraRectWidth) {
    const xPosition = this.#xTracking + marginLeft;
    const yPosition = this.#yTracking;
    const prefix = heading.prefix || '';
    const textContent = textBox.text || (prefix + heading.title);
    const shouldCreateLongText = this.#isLongTextCalculation(textContent, fontSize, letterSpacing);
    const style = {
      fontWeight,
      fontSize,
      fontFamily,
      fill,
      listWidth,
      listFill,
      bbox: textBox,
      x: xPosition,
      y: yPosition,
      isUpperCase: false,
      isSplit,
      prefix,
      letterSpacing
    };

    return shouldCreateLongText
      ? this.#createLongTextElement(textContent, style)
      : this.#createTextElement(textContent, style, extraRectWidth);
  }

  #offsetPageNumber (heading) {
    if (heading?.page_number) return transformNumberToStyle(heading.page_number, 'numbers');

    const pageNumberIndex = parseInt(heading.index) + this.#pageNumberOffset + '';
    if (heading.page_number_style === 'none' || !heading.page_number) return pageNumberIndex;

    const result = transformNumberToStyle(parseInt(heading.index) + this.#pageNumberOffset, heading.page_number_style);

    return result || pageNumberIndex;
  }

  #drawPageNumber (heading, { fontWeight, pageNumberFontSize, fontFamily, pageNumberBox, textBox, marginLeft }) {
    if (!heading.title) return;

    const isPageNumber = typeof heading?.page_numbers === 'boolean'
      ? heading.page_numbers
      : this.#settings.isPageNumber;

    if (!isPageNumber) return;

    const fontSize = pageNumberFontSize;
    const shouldShiftToNextLine = textBox.lastLineWidth + marginLeft > this.#canvasWidth * this.#maxHeadingWidthPercent;
    const offsetX = shouldShiftToNextLine ? 0 : textBox.lastLineWidth + 2 * this.#elementPadding;

    const isAlignedRight = typeof heading?.align_page_numbers_right === 'boolean'
      ? heading.align_page_numbers_right
      : this.#settings.isAlignedRight;

    const xPosition = isAlignedRight
      ? this.#canvasWidth - this.#offset - pageNumberBox.width
      : this.#xTracking + marginLeft + offsetX;

    const yPosition = shouldShiftToNextLine ? this.#yTracking + textBox.height : this.#yTracking + textBox.height - fontSize;
    const pageNumberIndex = parseInt(heading.index) + this.#pageNumberOffset + '';
    const pageNumberContent = this.#offsetPageNumber(heading);
    const style = {
      fontWeight: 400,
      fontSize,
      fontFamily,
      bbox: pageNumberBox,
      x: xPosition,
      y: yPosition,
      isUpperCase: false
    };

    const isLinkUsed = typeof heading?.use_links === 'boolean'
      ? heading.use_links
      : this.#settings.isLinkUsed;

    return isLinkUsed
      ? this.#createHyperLinkTextElement(pageNumberContent, pageNumberIndex, style)
      : this.#createTextElement(pageNumberContent, style);
  }

  #drawLeader (heading, { fontSize, marginLeft, textBox, pageNumberBox, listWidth }) {
    if (!heading.title) return;

    const leaderStyle = heading.leader || this.#settings.leaderStyle || 'dashes';
    if (leaderStyle === 'none') return;

    const shouldShiftToNextLine = textBox.lastLineWidth + marginLeft > this.#canvasWidth * this.#maxHeadingWidthPercent;
    const offsetX = shouldShiftToNextLine ? listWidth || 0 : textBox.lastLineWidth + this.#elementPadding;
    const offsetY = shouldShiftToNextLine ? textBox.height + fontSize : textBox.height;
    const x1 = this.#xTracking + marginLeft + offsetX;
    const y1 = this.#yTracking + offsetY;
    const x2 = this.#canvasWidth - this.#offset - pageNumberBox.width - this.#elementPadding;
    const y2 = this.#yTracking + offsetY;

    const style = { leaderStyle };

    return this.#createLineElement(style, { x1, y1, x2, y2 });
  }

  #createHyperLinkTextElement (textContent, index, { fontWeight, fontSize, fontFamily, fill, bbox, x, y, isUpperCase }) {
    const pageNumber = parseInt(index) ?? 1;
    const a = this.#canvas.addSvgElementFromJson({
      element: 'a',
      attr: {
        id: this.#canvas.getNextId(),
        'data-meta': 'toc',
        class: 'element group',
        'xlink:href': `#${pageNumber}`
      }
    }, this.#currentPage.querySelector('g.main'));

    const title = this.#canvas.addSvgElementFromJson({
      element: 'title',
      attr: {
        id: this.#canvas.getNextId(),
        class: 'coords'
      }
    }, a);

    const textarea = this.#canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        id: this.#canvas.getNextId(),
        class: 'textarea subelement '
      }
    }, a);

    const rect = this.#canvas.addSvgElementFromJson({
      element: 'rect',
      attr: {
        id: this.#canvas.getNextId(),
        fill: 'none',
        x,
        y,
        width: bbox.width + config.tableOfContents.extraRectWidth,
        height: bbox.height
      }
    }, textarea);

    const g = this.#canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        id: this.#canvas.getNextId(),
        class: 'text',
        fill: fill || '#000000',
        'font-family': fontFamily,
        'font-size': fontSize,
        'font-weight': fontWeight,
        'text-anchor': 'start'
      }
    }, textarea);

    const text = this.#canvas.addSvgElementFromJson({
      element: 'text',
      attr: { class: ' eol', x, y: y + fontSize }
    }, g);

    text.textContent = isUpperCase ? textContent.toUpperCase() : textContent;
    title.textContent = `${rect.getAttribute('x')} ${rect.getAttribute('y')} ${rect.getAttribute('width')} ${rect.getAttribute('height')}`;
  }

  #createTextElement (textContent, style, extraRectWidth) {
    const {
      fontWeight,
      fontSize,
      fontFamily,
      fill,
      bbox,
      x,
      y,
      isUpperCase,
      letterSpacing,
      prefix
    } = style;

    const textarea = this.#canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        id: this.#canvas.getNextId(),
        'data-meta': 'toc',
        class: 'element textarea'
      }
    }, this.#currentPage.querySelector('g.main'));

    this.#canvas.addSvgElementFromJson({
      element: 'rect',
      attr: {
        id: this.#canvas.getNextId(),
        fill: 'none',
        x,
        y,
        width: bbox.width + (extraRectWidth ?? config.tableOfContents.extraRectWidth),
        height: bbox.height
      }
    }, textarea);

    const textClass = letterSpacing ? `text letter-spacing-${letterSpacing} ` : 'text ';
    const textDx = letterSpacing ? [0].concat(new Array(textContent.length - 2).fill(letterSpacing)).join(' ') : '';
    const textAttribute = textDx
      ? { class: ' eol', x, y: y + fontSize, dx: textDx }
      : { class: ' eol', x, y: y + fontSize };

    const g = this.#canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        id: this.#canvas.getNextId(),
        class: textClass,
        fill: fill || '#000000',
        'font-family': fontFamily,
        'font-size': fontSize,
        'font-weight': fontWeight,
        'text-anchor': 'start'
      }
    }, textarea);

    const text = this.#canvas.addSvgElementFromJson({
      element: 'text',
      attr: textAttribute
    }, g);

    const content = isUpperCase ? textContent.toUpperCase() : textContent;
    text.innerHTML = this.#calculateInnerHTML(content, prefix, style, !!prefix?.length);

    return null;
  }

  #createLongTextElement (textContent, style) {
    const {
      fontWeight,
      fontSize,
      fontFamily,
      fill,
      bbox,
      x,
      y,
      isUpperCase,
      listWidth,
      isSplit,
      prefix
    } = style;

    const textarea = this.#canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        id: this.#canvas.getNextId(),
        'data-meta': 'toc',
        class: 'element textarea'
      }
    }, this.#currentPage.querySelector('g.main'));

    this.#canvas.addSvgElementFromJson({
      element: 'rect',
      attr: {
        id: this.#canvas.getNextId(),
        fill: 'none',
        x,
        y,
        width: bbox.width,
        height: bbox.height
      }
    }, textarea);

    const textClass = 'text ';
    const firstLineMargin = isSplit ? '' : 'first-line-margin-0.1 ';
    const lineMargin = listWidth ? `line-margin-${listWidth} ` : '';

    const g = this.#canvas.addSvgElementFromJson({
      element: 'g',
      attr: {
        id: this.#canvas.getNextId(),
        class: textClass + firstLineMargin + lineMargin,
        fill: fill || '#000000',
        'font-family': fontFamily,
        'font-size': fontSize,
        'font-weight': fontWeight,
        'text-anchor': 'start'
      }
    }, textarea);

    const lineHeight = bbox.height / bbox.lines.length;
    let renderedLineIndex = 0;
    for (let i = 0; i < bbox.lines.length; i++) {
      const lineListWidth = i === 0 && !isSplit ? 0 : listWidth;
      const isNextPage = y + lineHeight * (i + 2) > this.#canvasHeight * (1 - this.#offsetPercent) && (i + 2 <= bbox.lines.length);
      const textClass = i === bbox.lines.length - 1 || isNextPage ? ' eol' : '';
      const textAttribute = { class: textClass, x: x + lineListWidth, y: y + lineHeight * (i + 1) };
      const text = this.#canvas.addSvgElementFromJson({
        element: 'text',
        attr: textAttribute
      }, g);

      const content = isUpperCase ? bbox.lines[i].toUpperCase() : bbox.lines[i];

      text.innerHTML = this.#calculateInnerHTML(content, prefix, style, !!listWidth);
      renderedLineIndex = i;
      // If the heading to long, split it and add heading to next TOC page
      if (isNextPage) break;
    }

    return renderedLineIndex === bbox.lines.length - 1
      ? null
      : {
          width: bbox.width,
          height: fontSize * (bbox.lines.length - 1 - renderedLineIndex),
          text: bbox.lines.slice(renderedLineIndex + 1).join(''),
          lines: bbox.lines.slice(renderedLineIndex + 1),
          lastLineWidth: bbox.lastLineWidth
        };
  }

  #calculateInnerHTML (content, prefix, style, isList) {
    if (!isList) return content;
    if (!prefix?.length) return content;

    const listFill = style.listFill ? `fill="${style.listFill}"` : '';
    const innerHTML = `<tspan ${listFill}>${content.slice(0, prefix.length)}</tspan><tspan>${content.slice(prefix.length)}</tspan>`;

    return innerHTML;
  }

  #createLineElement (style, { x1, y1, x2, y2 }) {
    if (x1 >= x2) return;
    if (style.leaderStyle === 'none') return;

    const dotStrokeWith = this.#ratio > 1 ? 2 : 2 * this.#ratio;
    const lineStrokeWith = this.#ratio > 2 ? 2 : 1 * this.#ratio;

    const styleAttribute = {
      dots: {
        'stroke-width': dotStrokeWith,
        'stroke-linecap': 'round',
        'stroke-dasharray': `${0.0001 * dotStrokeWith} ${2 * dotStrokeWith}`
      },
      dashes: {
        'stroke-width': lineStrokeWith,
        'stroke-linecap': 'round',
        'stroke-dasharray': `${8 * lineStrokeWith} ${5 * lineStrokeWith}`
      },
      line: {
        'stroke-width': lineStrokeWith,
        'stroke-linecap': 'round'
      }
    };

    this.#canvas.addSvgElementFromJson({
      element: 'line',
      attr: Object.assign({}, {
        id: this.#canvas.getNextId(),
        class: 'element',
        'data-meta': 'toc',
        fill: 'none',
        stroke: '#000000',
        'stroke-opacity': 1,
        'fill-opacity': 1,
        opacity: 1,
        x1,
        y1,
        x2,
        y2
      }, styleAttribute[style.leaderStyle])
    }, this.#currentPage.querySelector('g.main'));
  }

  #getAttribute (heading, attribute) {
    const headingAttribute = heading[attribute];
    if (headingAttribute) return headingAttribute;

    const defaultStyle = this.#defaultHeadingStyle[heading.heading]?.[camelCase(attribute)];
    if (!defaultStyle) return null;

    if (attribute.includes('font_weight')) return defaultStyle;

    const isDefaultStyleNumber = !isNaN(Number(defaultStyle));
    if (isDefaultStyleNumber) return Number(defaultStyle) * this.#ratio;

    return defaultStyle;
  }

  #getStyle (heading) {
    const attributes = [
      'custom_font_id',
      'font_stretch',
      'fill',
      'font_family',
      'font_size',
      'font_style',
      'font_weight',
      'list_custom_font_id',
      'list_fill',
      'list_font_family',
      'list_font_size',
      'list_font_style',
      'list_font_weight'
    ];

    const style = {};
    for (const attribute of attributes) {
      const styleName = camelCase(attribute);
      style[styleName] = this.#getAttribute(heading, attribute);
    }

    return style;
  }

  #getPageNumberFontSize (heading) {
    const fontSize = this.#defaultHeadingStyle.pageNumber.fontSize * this.#ratio;
    return Math.max(fontSize, 2);
  }

  #getMarginLeft (heading) {
    return heading.marginLeft || this.#defaultHeadingStyle[heading.heading].marginLeft * this.#ratio;
  }

  #getMarginBottom (heading) {
    return heading.marginBottom * this.#ratio || this.#defaultHeadingStyle[heading.heading].marginBottom * this.#ratio;
  }

  #getMarginTop (heading) {
    return heading.marginTop * this.#ratio || this.#defaultHeadingStyle[heading.heading].marginTop * this.#ratio;
  }

  #getIsUpperCase (heading) {
    return !!heading.isUpperCase;
  }

  #getLetterSpacing (heading) {
    // return heading.heading === 1 && this.#settings.tableFormat === 'classic' ? 3 : 0;
    return 0;
  }

  #calculateTextBox (text, style) {
    const {
      fontWeight,
      fontSize,
      fontFamily,
      isUpperCase,
      letterSpacing
    } = style;

    this.#context.font = `${fontWeight} ${fontSize}px "${fontFamily.replace(/['"]+/g, '')}"`;
    const maxWidth = this.#canvasWidth * this.#maxHeadingWidthPercent;
    const paragraph = isUpperCase
      ? text.toString().replace(/\n/g, config.text.noBreakSpace).toUpperCase()
      : text.toString().replace(/\n/g, config.text.noBreakSpace);
    const words = paragraph.split(/\s/);

    let outputText = '';
    let tempText = '';
    for (let wordIndex = 0; wordIndex < words.length; wordIndex++) {
      tempText += words[wordIndex] + config.text.noBreakSpace;
      if (this.#context.measureText(tempText).width > maxWidth - letterSpacing * (tempText.length - 2)) break;
      outputText += words[wordIndex] + config.text.noBreakSpace;
    }

    const metrics = this.#context.measureText(outputText);
    const width = metrics.width + letterSpacing * (outputText.length - 2);
    return { width, height: fontSize, text: outputText, lastLineWidth: width, lines: [outputText] };
  }

  #calculateLongTextBox (text, style) {
    const {
      fontWeight,
      fontSize,
      fontFamily,
      isUpperCase,
      marginLeft,
      listWidth
    } = style;

    this.#context.font = `${fontWeight} ${fontSize}px "${fontFamily.replace(/['"]+/g, '')}"`;
    const textarea = {
      font_size: fontSize,
      font_family: fontFamily,
      font_weight: fontWeight,
      styles: []
    };

    const maxWidth = this.#canvasWidth * (1 - 2 * this.#offsetPercent) - marginLeft;
    const paragraph = isUpperCase
      ? text.toString().replace(/\n/g, config.text.noBreakSpace).toUpperCase()
      : text.toString().replace(/\n/g, config.text.noBreakSpace);
    const words = paragraph.split(/\s/);

    const lines = [];

    let line = '';
    for (let i = 0; i < words.length; i++) {
      const currentLine = line + words[i] + config.text.noBreakSpace;
      const currentLineWidth = INKIVE.Text.Helpers.getLineBBox(textarea, 0, currentLine).width;
      const listWidthOfFirstLine = lines.length === 0 ? 0 : listWidth;

      if (currentLineWidth > maxWidth - listWidthOfFirstLine && i > 0) {
        lines.push(line);
        line = words[i] + config.text.noBreakSpace;
      } else {
        line = currentLine;
      }
    }
    lines.push(line);
    const listWidthOfLastLine = lines.length === 1 ? 0 : listWidth;
    const lastLineWidth = this.#context.measureText(line).width + listWidthOfLastLine;
    const boxWidth = lines.length > 1 ? maxWidth : lastLineWidth;

    return { width: boxWidth, height: fontSize * lines.length, text, lines, lastLineWidth };
  }

  #recalculatePositionTracking (style) {
    const position = this.#positions[this.#currentPageIndex] || {};
    const lineHeight = Math.max(style.textBox.height, style.pageNumberBox.height);
    const shouldShiftToNextLine = style.textBox.lastLineWidth + style.marginLeft > this.#canvasWidth * this.#maxHeadingWidthPercent;
    const offsetY = shouldShiftToNextLine ? lineHeight + style.fontSize + style.marginBottom : lineHeight + style.marginBottom;
    const yLimit = position.width && position.height
      ? position.y + position.height
      : this.#canvasHeight - this.#canvasHeight * this.#offsetPercent;

    this.#yTracking = this.#yTracking + offsetY;

    if (this.#yTracking <= yLimit) return;

    this.#currentPageIndex += 1;
    if (this.#currentPageIndex > config.tableOfContents.maxPages - 1) return;
    this.#currentPage = this.#tableOfContentsPages[this.#currentPageIndex];

    const currentPageSize = this.#calculatePageSize(this.#currentPage);
    this.#calculateTableMetrics(currentPageSize.width, currentPageSize.height);
  }

  #calculatePageSize (svgElement) {
    return {
      width: Number(svgElement.getAttribute('width')) || this.#defaultWidth,
      height: Number(svgElement.getAttribute('height')) || this.#defaultHeight
    };
  }
}
